import { useCallback, useId, useSyncExternalStore } from 'react'

/**
 * 同じ z 層に並ぶフローティングウィンドウの前後関係を共有するストア。
 * 「最後に前面化した窓」の識別子だけを保持し、各窓は自分が最前面かどうかで
 * 0/1 の z-index オフセットを決める。オフセットを 0/1 に限定することで、
 * クリックを重ねても上位の z 層（経験値演出・チュートリアル・モーダル）へ
 * 数値が到達せず突き抜けない。
 */
let frontId = ''
const listeners = new Set<() => void>()

const getFrontId = (): string => frontId

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const setFront = (id: string): void => {
  frontId = id
  for (const listener of listeners) {
    listener()
  }
}

export interface BringToFront {
  /** 自ウィンドウの z-index オフセット。最前面なら 1、それ以外は 0。 */
  readonly order: number
  /** 自ウィンドウを最前面へ持ち上げる。mousedown などの操作で呼ぶ。 */
  readonly bringToFront: () => void
}

/**
 * フローティングウィンドウを「最後に触った順」で前面に出すためのフック。
 *
 * 返す {@link BringToFront.order} を `calc(var(--z-video-window) + order)` の
 * ように z-index へ加算し、操作時に {@link BringToFront.bringToFront} を呼ぶと、
 * 最後に操作した窓が最前面へ来る。前後関係はモジュール共有ストアで管理し、
 * 全ウィンドウへ {@link useSyncExternalStore} で通知する。
 *
 * `bringToFront` はイベントハンドラから呼ぶこと（レンダー/エフェクト中に呼ぶと
 * 描画のたびに最前面が入れ替わってしまう）。
 */
export const useBringToFront = (): BringToFront => {
  const id = useId()
  const currentFrontId = useSyncExternalStore(subscribe, getFrontId)
  const bringToFront = useCallback(() => {
    setFront(id)
  }, [id])
  return { order: id === currentFrontId ? 1 : 0, bringToFront }
}
